'use client';

import { useFestivalContext } from '@/contexts/FestivalContext';
import type { TicketingFestival } from '@/utils/early-bird';
import { parseFestivalSlug } from '@/utils/festival-slug';
import { createClient } from '@/utils/supabase/client';
import { useEffect, useMemo, useState } from 'react';

type Festival = TicketingFestival;

export function useFestival() {
    const { slug, festival: initialFestival } = useFestivalContext();
    const [festival, setFestival] = useState<Festival | null>(initialFestival || null);
    const [loading, setLoading] = useState(!initialFestival);
    const [error, setError] = useState<Error | null>(null);
    const supabase = createClient();
    const slugIdentifier = useMemo(() => parseFestivalSlug(slug), [slug]);

    useEffect(() => {
        if (!slugIdentifier) {
            setLoading(false);
            setFestival(null);
            return;
        }

        if (initialFestival && festival?.id === initialFestival.id) {
            setLoading(false);
            return;
        }

        const fetchFestival = async () => {
            try {
                const { data, error: fetchError } = await supabase
                    .from('festivals')
                    .select('*')
                    .eq('type', slugIdentifier.type)
                    .eq('year', slugIdentifier.year)
                    .maybeSingle();

                if (fetchError) {
                    throw fetchError;
                }

                setFestival(data);
            } catch (err) {
                setError(err as Error);
                console.error('Error fetching festival:', err);
            } finally {
                setLoading(false);
            }
        };

        if (!initialFestival) {
            fetchFestival();
        }
    }, [supabase, slugIdentifier, initialFestival, festival?.id]);

    return { festival, loading, error };
}
